<template>
  <div>
    <!-- Policies and procedures table -->
    <div v-if="isGranted([permissions.PROCESS_POLICY_VIEW, permissions.PROCESS_POLICY_VIEW_OWN], process)"
         class="card"
         qid="policies-section"
         id="update_policies_and_procedures"
    >
      <div class="card-body" :class="{'pr-0 pl-0': showOnTask}">
        <div class="row">
          <div class="col-sm-6">
            <h5 qid="policy-table-title" class="mb-2" v-if="!showOnTask">
              {{ $t('processes.policies.title') }}
            </h5>

            <span v-if="!showOnTask">
              <span class="cursor-pointer task-label-open mr-2"
                    v-if="getTask('update_policies_and_procedures')"
              >
                <UpdateSingleTaskModal
                    :task="getTask('update_policies_and_procedures')"
                    @reloadContent="reloadContent"
                    :editIcon="true"
                ></UpdateSingleTaskModal>
              </span>
              <span v-if="getGuide('update_policies_and_procedures')">
                <GuideButton :guide="getGuide('update_policies_and_procedures')"></GuideButton>
              </span>
            </span>
          </div>
          <div class="col-sm-6 text-right">
            <span class="btn btn-success"
                  qid="policy-create-button"
                  @click="showCreatePolicyModal"
                  v-if="isGranted([permissions.PROCESS_POLICY_CREATE, permissions.PROCESS_POLICY_CREATE_OWN], process)"
            >
              <font-awesome-icon icon="plus"/> {{ $t('processes.policies.create_button') }}
            </span>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('processes.policies.table.name') }}
            </th>
            <th scope="col">
              {{ $t('maintenance.policies.table.category') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="130"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="object in policyTableItems">
            <td scope="row">
              <span v-if="object.policy"
                    @click="openViewPolicyModal(object.policy)"
                    qid="policy-name-link"
                    class="btn btn-link text-left cursor-pointer"
              >
                {{ object.policy.name }}
              </span>
            </td>
            <td>
              <span qid="policy-category"
                    :title="$t('maintenance.policies.category.' + $options.filters.slugify(object.policy.category))"
                    v-if="object.policy.category"
                    class="text-normal"
              >
                <font-awesome-icon icon="shapes"/>
                {{ $t('maintenance.policies.category.' + $options.filters.slugify(object.policy.category)) }}
              </span>
            </td>
            <td>
              <span class="text-normal">
                {{ object.policy.updated_at | formatDate }}
              </span>
            </td>
            <td>
              <span v-if="object.policy"
                    @click="openViewPolicyModal(object.policy)"
                    qid="view-policy-button"
                    class="btn btn-view"
              >
                <font-awesome-icon icon="eye"/>
              </span>
              <span v-if="isGranted([permissions.PROCESS_POLICY_DELETE, permissions.PROCESS_POLICY_DELETE_OWN], process)"
                      class="btn btn-delete"
                      qid="delete-policy-modal"
                      @click="showPolicyConfirmDeleteModal(object)"
              >
                <font-awesome-icon icon="trash-alt"/>
              </span>
            </td>
          </tr>
          <tr v-if="!policyLoading && !policyTableItems.length" qid="no-results-row" class="text-center">
            <td colspan="10">
              <h5 class="mt-3">
                {{ $t('system.no_results') }}
              </h5>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>


    <!-- Delete policy modal template -->
    <b-modal ref="delete-policy" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-lb-title">
          {{ $t('processes.policies.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('processes.policies.delete.description') }}
        <span v-if="itemToDelete.policy">{{ itemToDelete.policy.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('processes.policies.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hidePolicyConfirmDeleteModal"
                  qid="delete-policy-cancel-button"
        >
          {{ $t('processes.policies.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeletePolicy(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-policy-submit-button"
                  v-if="isGranted([permissions.PROCESS_POLICY_DELETE, permissions.PROCESS_POLICY_DELETE_OWN], process)"
        >
          {{ $t('processes.policies.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new policy -->
    <b-modal ref="add-policy" hide-footer @hide="hideCreatePolicyModal">
      <div class="d-block text-center">
        <h4 qid="add-io-title">
          {{ $t('processes.policies.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-io-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="policyFormSubmit" qid="create-policy-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-policy-pg-label">
              {{ $t('processes.policies.create.policy_label') }}
            </label>
            <multiselect
                  v-model="selectedPolicy"
                  :options="policyItems"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  :placeholder="$t('processes.policies.create.policy_placeholder')"
                  label="name"
                  @search-change="loadPolicy"
                  track-by="slug"
                  qid="create-lb-form-pg"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedPolicy && selectedPolicy.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showPolicyDescription = !showPolicyDescription"
                    qid="expand-policy-description"
              >
                {{$t('processes.policies.create.policy_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showPolicyDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showPolicyDescription"/>
            </span>
          </div>
          <div v-if="selectedPolicy && selectedPolicy.description && showPolicyDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedPolicy.description }}
            </span>
          </div>
          <div v-if="selectedPolicy && selectedPolicy.implementation_status" class="mb-3">
            <label class="mt-3">
                {{ $t('maintenance.policies.create.implementation_status_label') }}
            </label>
            <br>
            <span v-if="selectedPolicy">
              {{ selectedPolicy.implementation_status }}
            </span>
          </div>
          <div v-if="selectedPolicy && selectedPolicy.category" class="mb-3">
            <label>
              {{ $t('maintenance.policies.create.category_label') }}
            </label>
            <br>
            <span v-if="selectedPolicy">
              {{ $t('maintenance.policies.category.' + $options.filters.slugify(selectedPolicy.category)) }}
            </span>
          </div>
          <b-form-group v-if="selectedPolicy && selectedPolicy.media && selectedPolicy.media.length">
            <label qid="create-tp-attachments-label">
              {{ $t('maintenance.third_parties.create.attachments') }}
            </label>
            <br>
            <p v-for="file in selectedPolicy.media" :key="file.file_name">
              <span class="mr-3 float-right text-muted cursor-pointer"
                    @click="downloadMedia(file)"
                    qid="download-media"
              >
                <font-awesome-icon icon="cloud-download-alt" />
              </span>
              <a @click="downloadMedia(file)" qid="create-tp-media-item">
                {{ file.file_name }}
              </a>
            </p>
          </b-form-group>
          <b-form-group v-if="selectedPolicy && selectedPolicy.links && selectedPolicy.links.length">
            <label qid="create-tp-link-label">
              {{ $t('maintenance.third_parties.create.links_label') }}
            </label>
            <p v-for="(link, index) in selectedPolicy.links">
              <a :href="link.url" target="_blank" qid="create-tp-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreatePolicyModal"
                    qid="create-p-cancel-button"
          >
            {{ $t('processes.policies.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('processes.policies.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <PolicyViewModal
        :object="selectedPolicy"
        :showModal="viewPolicyModal"
        @closeViewModal="handleCloseViewPModal"
    ></PolicyViewModal>


  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import PolicyViewModal from '@/views/Policies/PolicyViewModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'PoliciesAndProceduresTable',
    data: function() {
      return {
        // policies
        policyTableItems: [],
        policyLoading: true,
        showPolicyDescription: false,
        policyItems: [],
        selectedPolicy: [],
        viewPolicyModal: false,
        process: {},

        error: '',
        loadingItems: false,
        selectedDS: [],
        selectedDSSort: '',
        selectedDSDirection: '',
        acValidationShow: false,
        acTableItems: [],
        acLoading: true,
        dsItems: [],
        updateValidationList: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: {},
        disabledValidationOptions: [],
        showDSDescription: false,
        buttonDisabled: false,
        selectedDataSubject: {},
        viewDataSubjectModal: false,
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      PolicyViewModal,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {

      },
      object: function (value) {
        if (value && value.slug) {
          this.process = value
          this.loadPolicies();
          this.loadTasks('update_policies_and_procedures');
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
              this.process = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_policies_and_procedures')
        }
      },
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      reloadContent() {
        this.loadPolicies()
        this.loadTasks('update_policies_and_procedures');
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.process.who_am_i,
              model_id: this.process.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      handleCloseViewPModal () {
        this.viewPolicyModal = false;
        this.selectedPolicy = []
      },
      openViewPolicyModal(object) {
        this.selectedPolicy = object
        this.viewPolicyModal = true
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Process' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Process',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showCreatePolicyModal() {
        this.loadPolicy()
        this.$refs['add-policy'].show()
      },
      hideCreatePolicyModal() {
        this.error = ''
        this.selectedPolicy = [];
        this.showPolicyDescription = false;
        this.$refs['add-policy'].hide()
      },
      showPolicyConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-policy'].show()
      },
      hidePolicyConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-policy'].hide()
      },
      loadContent() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.process = response.data;
        }, function() {});
      },
      confirmDeletePolicy(object) {
        let self = this;
        piincHttp.delete('processes/' + this.process.slug + '/policies/' + object.id).then(function() {
          self.hidePolicyConfirmDeleteModal();
          self.loadPolicies();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('processes.policies.delete.success'))
        }, function() {});
      },
      policyFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedPolicy && this.selectedPolicy.id) {
          formData.append("policy_id", this.selectedPolicy.id);
        }

        formData.append("process", this.process.slug);

        piincHttp.post('/processes/' + this.process.slug + '/policies', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('processes.policies.create.success'))
          self.hideCreatePolicyModal();
          self.loadPolicies();
          self.loadContent();
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.policy_id) {
              self.error += ' ' + error.data.errors.policy_id[0]
            }
          }
        })
      },
      // Table list
      loadPolicies() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug + '/policies', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.policyTableItems = response.data;
          self.policyLoading = false;
        }, function() {});
      },
      loadPolicy(query) {
        let self = this;
        piincHttp.get('policies', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.policyItems = response.data.items
          if (self.policyItems && !self.policyItems.length && !query) {
            self.policyItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    created () {
      this.process = this.object;
      if (this.getClient && this.getClient.slug) {
        this.loadPolicy();
      }
      // Table items
      if (this.process && this.process.slug) {
        this.loadPolicies();
        this.loadTasks('update_policies_and_procedures');
      }

      this.loadGuides('update_policies_and_procedures');

    }
  }
</script>
